import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { PlusCircle, Edit, Trash2, Clock, User, Car, Info, Award, X, ChevronDown, ChevronUp, GripVertical, Users, CheckCircle2, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import RegistrationsDialog from './RegistrationsDialog';
import EventAttendanceDialog from './EventAttendanceDialog';
import EventLocationsManager from './EventLocationsManager';
import BriteLabRegistrantsDialog from './BriteLabRegistrantsDialog';
import BriteLabReportDialog from './BriteLabReportDialog';
import { stableAgendaItemId } from '@/lib/agendaUtils';
import RichTextEditor from '@/components/RichTextEditor';

const BACKEND_URL = '';

const emptyForm = {
  title: '', description: '', agenda_intro: '', date: '', start_time: '', end_time: '', location: '', format: 'in-person',
  topic_tags: '', max_delegates: '', ticket_price: '', ticket_currency: 'gbp',
  agenda: [], speakers_detail: [], parking_info: '', additional_info: '', event_sponsors: [],
  // Networking-focused config: predefined meeting points shown as a
  // dropdown in the Request-to-Meet dialog (empty = free text), and the
  // allowed meeting durations in minutes (empty = default 15 & 30).
  meeting_points: '', meeting_durations: '',
  // Keynotes that attendees of this event get auto-access to. Stored as
  // an array of keynote_id strings. UI is a checkbox list of all keynotes.
  linked_keynote_ids: [],
};

const emptySpeaker = { name: '', bio: '', headshot_url: '', user_id: '' };
const emptyAgendaSpeaker = { user_id: '', name: '', headshot_url: '', bio: '' };
const emptyAgenda = { time: '', title: '', speakers: [], room: '', description: '' };
const emptySponsor = { name: '', logo_url: '', website: '' };

export function AdminEventsTab() {
  const [events, setEvents] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [britelabReportFor, setBritelabReportFor] = useState(null);
  const [editingLabCounts, setEditingLabCounts] = useState({});
  useEffect(() => {
    if (!editing?.event_id) { setEditingLabCounts({}); return; }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${editing.event_id}/britelab/counts`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null)).then(d => setEditingLabCounts(d?.counts || {})).catch(() => {});
  }, [editing?.event_id]);
  const [form, setForm] = useState({ ...emptyForm });
  const [expandedCard, setExpandedCard] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [speakerDragIdx, setSpeakerDragIdx] = useState(null);
  const [allMembers, setAllMembers] = useState([]);
  const [speakerSearch, setSpeakerSearch] = useState({});
  const [agendaSpeakerSearch, setAgendaSpeakerSearch] = useState({});
  const [registrationsFor, setRegistrationsFor] = useState(null);
  const [viewingRegistrants, setViewingRegistrants] = useState(null);  // {event_id, item}
  const [attendanceFor, setAttendanceFor] = useState(null);
  // Keynotes available to link from this event ("attendees of X also get
  // access to keynote Y"). Loaded once for the admin form.
  const [allKeynotes, setAllKeynotes] = useState([]);
  // Historic-attendee bulk-add modal state
  const [historicFor, setHistoricFor] = useState(null);
  const [historicEmails, setHistoricEmails] = useState('');
  const [historicBusy, setHistoricBusy] = useState(false);
  const [historicResult, setHistoricResult] = useState(null);

  useEffect(() => { load(); loadMembers(); loadKeynotes(); }, []);

  const load = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/events`, { credentials: 'include' });
      if (res.ok) setEvents(await res.json());
    } catch {}
  };

  const loadMembers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/members`, { credentials: 'include' });
      if (res.ok) setAllMembers(await res.json());
    } catch {}
  };

  const loadKeynotes = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/keynotes/admin`, { credentials: 'include' });
      if (res.ok) setAllKeynotes(await res.json());
    } catch {}
  };

  const submitHistoricAttendees = async () => {
    if (!historicFor) return;
    const emails = historicEmails
      .split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error('Add at least one email'); return; }
    setHistoricBusy(true);
    setHistoricResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/events/${historicFor.event_id}/historic-attendees`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setHistoricResult(data);
      toast.success(`${data.added} attendee${data.added === 1 ? '' : 's'} added`);
      load(); // refresh attendee counts
    } catch (e) { toast.error(e.message); }
    finally { setHistoricBusy(false); }
  };

  const linkMemberToSpeaker = (idx, member) => {
    updateItem('speakers_detail', idx, 'user_id', member.user_id);
    updateItem('speakers_detail', idx, 'name', member.name);
    if (member.picture) updateItem('speakers_detail', idx, 'headshot_url', member.picture);
    if (member.profile?.bio) updateItem('speakers_detail', idx, 'bio', member.profile.bio);
    setSpeakerSearch(prev => ({ ...prev, [idx]: '' }));
  };

  // When a speaker is tagged or named in the agenda, mirror them into the
  // event-level Speakers section below so the admin can fill in their full
  // bio / headshot in one place. Idempotent — matches by user_id when present,
  // otherwise by case-insensitive name. Existing entries are merged in-place
  // (we only fill in missing fields, never overwrite admin edits).
  const syncSpeakerToDetail = (spk) => {
    if (!spk || (!spk.user_id && !(spk.name || '').trim())) return;
    setForm(p => {
      const existing = p.speakers_detail || [];
      let merged = false;
      const next = existing.map(s => {
        const matchesByUid = spk.user_id && s.user_id === spk.user_id;
        const matchesByName = !spk.user_id && spk.name && (s.name || '').trim().toLowerCase() === spk.name.trim().toLowerCase();
        if ((matchesByUid || matchesByName) && !merged) {
          merged = true;
          return {
            ...s,
            user_id: s.user_id || spk.user_id || '',
            name: s.name || spk.name || '',
            headshot_url: s.headshot_url || spk.headshot_url || '',
            bio: s.bio || spk.bio || '',
          };
        }
        return s;
      });
      if (merged) return { ...p, speakers_detail: next };
      // Not found — append a fresh entry the admin can complete.
      return {
        ...p,
        speakers_detail: [...existing, {
          user_id: spk.user_id || '',
          name: spk.name || '',
          headshot_url: spk.headshot_url || '',
          bio: spk.bio || '',
        }],
      };
    });
  };

  // Multi-speaker agenda helpers — supports up to 8 speakers per agenda item.
  const addAgendaSpeaker = (agendaIdx) => {
    setForm(p => ({
      ...p,
      agenda: p.agenda.map((it, i) => i === agendaIdx
        ? { ...it, speakers: [...(it.speakers || []), { ...emptyAgendaSpeaker }] }
        : it),
    }));
  };
  const removeAgendaSpeaker = (agendaIdx, spkIdx) => {
    setForm(p => ({
      ...p,
      agenda: p.agenda.map((it, i) => i === agendaIdx
        ? { ...it, speakers: (it.speakers || []).filter((_, j) => j !== spkIdx) }
        : it),
    }));
  };
  const updateAgendaSpeaker = (agendaIdx, spkIdx, key, val) => {
    setForm(p => ({
      ...p,
      agenda: p.agenda.map((it, i) => i === agendaIdx
        ? { ...it, speakers: (it.speakers || []).map((s, j) => j === spkIdx ? { ...s, [key]: val } : s) }
        : it),
    }));
  };
  const linkMemberToAgendaSpeaker = (agendaIdx, spkIdx, member) => {
    const newSpeaker = {
      user_id: member.user_id,
      name: member.name,
      headshot_url: member.picture || '',
      bio: member.profile?.bio || '',
    };
    setForm(p => ({
      ...p,
      agenda: p.agenda.map((it, i) => i === agendaIdx
        ? {
          ...it,
          speakers: (it.speakers || []).map((s, j) => j === spkIdx ? newSpeaker : s),
        }
        : it),
    }));
    setAgendaSpeakerSearch(prev => ({ ...prev, [`${agendaIdx}-${spkIdx}`]: '' }));
    // Mirror into the Speakers section below so admin can complete bio/headshot.
    syncSpeakerToDetail(newSpeaker);
    toast.success(`${member.name} also added to Speakers section — complete their bio there.`);
  };
  const clearAgendaSpeakerLink = (agendaIdx, spkIdx) => {
    setForm(p => ({
      ...p,
      agenda: p.agenda.map((it, i) => i === agendaIdx
        ? { ...it, speakers: (it.speakers || []).map((s, j) => j === spkIdx ? { ...s, user_id: '', headshot_url: '', bio: '' } : s) }
        : it),
    }));
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }

    // Safety guard: warn before destructively reducing speakers_detail or agenda
    // (prevents accidental wipes after a deploy when an admin opens & saves the form).
    if (editing) {
      const origSpeakers = editing.speakers_detail?.length || 0;
      const origAgenda = editing.agenda?.length || 0;
      const newSpeakers = (form.speakers_detail || []).length;
      const newAgenda = (form.agenda || []).length;
      const speakersLost = Math.max(0, origSpeakers - newSpeakers);
      const agendaLost = Math.max(0, origAgenda - newAgenda);
      // Also flag if any speaker that originally had a bio or headshot is being saved blank
      const wipedBios = (editing.speakers_detail || []).filter((orig, i) => {
        const cur = (form.speakers_detail || [])[i];
        if (!cur) return false;
        const origHas = (orig.bio && orig.bio.trim()) || (orig.headshot_url && orig.headshot_url.trim());
        const curHas = (cur.bio && cur.bio.trim()) || (cur.headshot_url && cur.headshot_url.trim());
        return origHas && !curHas;
      }).length;
      if (speakersLost > 0 || agendaLost > 0 || wipedBios > 0) {
        const msgs = [];
        if (speakersLost > 0) msgs.push(`${speakersLost} speaker${speakersLost === 1 ? '' : 's'}`);
        if (agendaLost > 0) msgs.push(`${agendaLost} agenda item${agendaLost === 1 ? '' : 's'}`);
        if (wipedBios > 0) msgs.push(`bio/headshot on ${wipedBios} speaker${wipedBios === 1 ? '' : 's'}`);
        const ok = await window.appConfirm(`You're about to REMOVE ${msgs.join(', ')} from this event. Are you sure?`);
        if (!ok) return;
      }
    }

    try {
      const payload = {
        ...form,
        topic_tags: typeof form.topic_tags === 'string' ? form.topic_tags.split(',').map(t => t.trim()).filter(Boolean) : form.topic_tags,
        // Comma-separated UI → clean arrays going to the backend
        meeting_points: typeof form.meeting_points === 'string'
          ? form.meeting_points.split(',').map(t => t.trim()).filter(Boolean)
          : (form.meeting_points || []),
        meeting_durations: typeof form.meeting_durations === 'string'
          ? form.meeting_durations.split(',').map(t => parseInt(t.trim(), 10)).filter(n => Number.isFinite(n) && n >= 5 && n <= 240)
          : (form.meeting_durations || []),
        speakers: (form.speakers_detail || []).map(s => s.name).filter(Boolean),
        max_delegates: form.max_delegates ? parseInt(form.max_delegates) : null,
        ticket_price: form.ticket_price ? parseFloat(form.ticket_price) : null,
        ticket_currency: form.ticket_currency || 'gbp',
      };
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `${BACKEND_URL}/api/events/${editing.event_id}` : `${BACKEND_URL}/api/events`;
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      toast.success(editing ? 'Event updated!' : 'Event created!');
      setShowDialog(false); setEditing(null); setForm({ ...emptyForm }); load();
    } catch (e) { toast.error(e.message || 'Failed to save'); }
  };

  const del = async (id) => {
    if (!(await window.appConfirm('Delete this event permanently?'))) return;
    try {
      await fetch(`${BACKEND_URL}/api/events/${id}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Deleted'); load();
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (ev) => {
    setEditing(ev);
    // Migrate any legacy single-speaker agenda items to the new `speakers` array.
    // Items already using `speakers` pass through untouched.
    const migratedAgenda = (ev.agenda || []).map(item => {
      const out = migrateAgendaItem(item);
      return item.id ? { ...out, id: item.id } : out;
    });
    function migrateAgendaItem(item) {
      // BriteLab flags: carry through if present, else auto-detect by title.
      // Matches "BRITELAB -", "BRITELAB 1 -", "BRITELAB 5-", etc.
      const autoBrite = /^BRITELAB\s*\d*\s*-/i.test((item.title || '').trim());
      const briteFields = {
        is_britelab: item.is_britelab === undefined ? autoBrite : !!item.is_britelab,
        max_registrants: item.max_registrants || '',
      };
      if (Array.isArray(item.speakers) && item.speakers.length > 0) return { ...item, ...briteFields };
      const legacyName = item.speaker || '';
      const legacyUid = item.speaker_user_id || '';
      if (!legacyName && !legacyUid) {
        return { time: item.time || '', title: item.title || '', speakers: [], room: item.room || '', description: item.description || '', ...briteFields };
      }
      return {
        time: item.time || '', title: item.title || '',
        speakers: [{
          user_id: legacyUid,
          name: legacyName,
          headshot_url: item.speaker_headshot_url || '',
          bio: item.speaker_bio || '',
        }],
        room: item.room || '',
        description: item.description || '',
        ...briteFields,
      };
    }
    setForm({
      title: ev.title || '', description: ev.description || '', agenda_intro: ev.agenda_intro || '', date: ev.date || '',
      start_time: ev.start_time || '', end_time: ev.end_time || '',
      location: ev.location || '', format: ev.format || 'in-person',
      topic_tags: (ev.topic_tags || []).join(', '),
      meeting_points: (ev.meeting_points || []).join(', '),
      meeting_durations: (ev.meeting_durations || []).join(', '),
      max_delegates: ev.max_delegates || '', ticket_price: ev.ticket_price || '',
      ticket_currency: ev.ticket_currency || 'gbp',
      agenda: migratedAgenda,
      speakers_detail: ev.speakers_detail?.length ? ev.speakers_detail : (ev.speakers || []).map(name => ({ name, bio: '', headshot_url: '' })),
      parking_info: ev.parking_info || '', additional_info: ev.additional_info || '',
      event_sponsors: ev.event_sponsors || [],
      linked_keynote_ids: ev.linked_keynote_ids || [],
    });
    setShowDialog(true);
  };

  const openCreate = () => {
    setEditing(null); setForm({ ...emptyForm, agenda: [], speakers_detail: [], event_sponsors: [] }); setShowDialog(true);
  };

  // Dynamic list helpers
  const addItem = (field, empty) => setForm(p => ({ ...p, [field]: [...(p[field] || []), { ...empty }] }));
  const removeItem = (field, idx) => setForm(p => ({ ...p, [field]: p[field].filter((_, i) => i !== idx) }));
  const updateItem = (field, idx, key, val) => setForm(p => ({ ...p, [field]: p[field].map((item, i) => i === idx ? { ...item, [key]: val } : item) }));

  return (
    <div className="space-y-4" data-testid="admin-events-content">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold font-heading">Event Management</h2>
        <Button size="sm" onClick={openCreate} className="bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="create-event-button">
          <PlusCircle className="h-3.5 w-3.5 mr-1.5" /> Create Event
        </Button>
      </div>

      {/* Event List */}
      <div className="space-y-3">
        {events.map(ev => (
          <Card key={ev.event_id} data-testid={`admin-event-${ev.event_id}`} className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedCard(expandedCard === ev.event_id ? null : ev.event_id)}>
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-bold text-slate-800">{ev.title}</h4>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-navy/10 text-brand-navy font-semibold">{ev.format}</span>
                  {ev.ticket_price > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{(ev.ticket_currency || 'GBP').toUpperCase()} {ev.ticket_price}</span>}
                </div>
                <p className="text-xs text-slate-500">{ev.date}{ev.start_time ? ` ${ev.start_time}${ev.end_time ? `–${ev.end_time}` : ''}` : ''} &middot; {ev.location} &middot; {(ev.rsvps || []).length} attendee{(ev.rsvps || []).length !== 1 ? 's' : ''}</p>
                {expandedCard === ev.event_id && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-600">
                    {ev.agenda?.length > 0 && <p><strong>Agenda:</strong> {ev.agenda.length} items</p>}
                    {ev.speakers_detail?.length > 0 && <p><strong>Speakers:</strong> {ev.speakers_detail.map(s => s.name).join(', ')}</p>}
                    {ev.parking_info && <p><strong>Parking:</strong> {ev.parking_info.substring(0, 80)}...</p>}
                    {ev.event_sponsors?.length > 0 && <p><strong>Sponsors:</strong> {ev.event_sponsors.map(s => s.name).join(', ')}</p>}
                  </div>
                )}
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                {(ev.agenda || []).some(it => it.is_britelab || /^BRITELAB\s*\d*\s*-/i.test((it.title || '').trim())) && (
                  <Button size="sm" variant="ghost" onClick={() => setBritelabReportFor({ id: ev.event_id, title: ev.title })}
                    data-testid={`view-britelab-report-${ev.event_id}`} title="BriteLab registrations (view & download)"
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50">
                    <FlaskConical className="h-3.5 w-3.5" /><span className="ml-1 text-xs">BriteLabs</span>
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setRegistrationsFor({ id: ev.event_id, title: ev.title })} data-testid={`view-event-registrations-${ev.event_id}`} title="View registrations">
                  <Users className="h-3.5 w-3.5" />
                  <span className="ml-1 text-xs tabular-nums">{(ev.rsvps || []).length}</span>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setAttendanceFor({ id: ev.event_id, title: ev.title })}
                  data-testid={`manage-attendance-${ev.event_id}`}
                  title="Manage attendance (unlocks Keynotes)"
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setHistoricFor({ event_id: ev.event_id, title: ev.title }); setHistoricEmails(''); setHistoricResult(null); }}
                  data-testid={`add-historic-attendees-${ev.event_id}`}
                  title="Add historic attendees (bulk paste emails)"
                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                >
                  <Users className="h-3.5 w-3.5" />
                  <span className="ml-1 text-[10px]">+</span>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setExpandedCard(expandedCard === ev.event_id ? null : ev.event_id)}>
                  {expandedCard === ev.event_id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openEdit(ev)} data-testid={`edit-event-${ev.event_id}`}><Edit className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => del(ev.event_id)} data-testid={`delete-event-${ev.event_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </Card>
        ))}
        {events.length === 0 && <p className="text-center text-slate-400 py-8">No events yet.</p>}
      </div>

      {/* Event Form Dialog */}
      <Dialog open={showDialog} onOpenChange={(v) => { setShowDialog(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="event-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Event' : 'Create New Event'}</DialogTitle></DialogHeader>
          <div className="space-y-6">

            {/* Basic Info */}
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Info className="h-4 w-4" /> Basic Information</h3>
              <div className="space-y-3">
                <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" /></div>
                <div><Label>Description</Label><RichTextEditor value={form.description} onChange={v => setForm({ ...form, description: v })} testId="event-description-input" minHeight="6rem" placeholder="Overview shown on the event card and detail page" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="event-date-input" /></div>
                  <div><Label>Start Time</Label><Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} data-testid="event-start-time-input" /></div>
                  <div><Label>End Time</Label><Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} data-testid="event-end-time-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} data-testid="event-location-input" /></div>
                  <div>
                    <Label>Format</Label>
                    <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={form.format} onChange={e => setForm({ ...form, format: e.target.value })} data-testid="event-format-select">
                      <option value="in-person">In person</option>
                      <option value="online">Online</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>
                <div><Label>Topic Tags (comma-separated)</Label><Input value={form.topic_tags} onChange={e => setForm({ ...form, topic_tags: e.target.value })} placeholder="Sustainability, Net Zero, ESG" /></div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Meeting Points (comma-separated)</Label>
                    <Input
                      value={form.meeting_points}
                      onChange={e => setForm({ ...form, meeting_points: e.target.value })}
                      placeholder="Networking Zone A, Coffee Area, Partner Lounge"
                      data-testid="event-meeting-points-input"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">Shown as a dropdown when attendees request a meeting. Leave empty to allow free text.</p>
                  </div>
                  <div>
                    <Label>Meeting Durations (minutes, comma-separated)</Label>
                    <Input
                      value={form.meeting_durations}
                      onChange={e => setForm({ ...form, meeting_durations: e.target.value })}
                      placeholder="15, 30"
                      data-testid="event-meeting-durations-input"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">Durations attendees can request. Defaults to 15 and 30.</p>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                  <Label className="text-sm font-semibold">Agenda Locations (rooms)</Label>
                  <p className="text-[11px] text-slate-400 mb-2">Admin-managed per-event list. Each location becomes a filter tab on the public agenda page. Agenda rows still accept free text in the "Room" field for backward compatibility.</p>
                  <EventLocationsManager eventId={editing?.event_id} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Max Delegates</Label><Input type="number" value={form.max_delegates} onChange={e => setForm({ ...form, max_delegates: e.target.value })} placeholder="Unlimited" data-testid="event-max-delegates" /></div>
                  <div><Label>Ticket Price</Label><Input type="number" step="0.01" value={form.ticket_price} onChange={e => setForm({ ...form, ticket_price: e.target.value })} placeholder="0 = Free" data-testid="event-ticket-price" /></div>
                  <div>
                    <Label>Currency</Label>
                    <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={form.ticket_currency} onChange={e => setForm({ ...form, ticket_currency: e.target.value })}>
                      <option value="gbp">GBP</option><option value="usd">USD</option><option value="eur">EUR</option>
                    </select>
                  </div>
                </div>
              </div>
            </section>

            {/* Agenda */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Clock className="h-4 w-4" /> Agenda</h3>
                <Button size="sm" variant="outline" onClick={() => addItem('agenda', emptyAgenda)} data-testid="add-agenda-btn">
                  <PlusCircle className="h-3.5 w-3.5 mr-1" /> Add Item
                </Button>
              </div>
              <div className="mb-3">
                <Label className="text-xs text-slate-500">Agenda overview (optional — shown above the timeline)</Label>
                <RichTextEditor
                  value={form.agenda_intro || ''}
                  onChange={v => setForm({ ...form, agenda_intro: v })}
                  placeholder="Introduce the day — themes, tracks, running order, dress code…"
                  testId="event-agenda-intro-input"
                  minHeight="5rem"
                />
              </div>
              {(form.agenda || []).length === 0 && <p className="text-xs text-slate-400 italic">No agenda items yet.</p>}
              <div className="space-y-2">
                {(form.agenda || []).map((item, idx) => (
                  <div key={idx}
                    draggable
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-brand-navy/30'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-brand-navy/30'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('ring-2', 'ring-brand-navy/30');
                      if (dragIdx === null || dragIdx === idx) return;
                      const items = [...(form.agenda || [])];
                      const [moved] = items.splice(dragIdx, 1);
                      items.splice(idx, 0, moved);
                      setForm(p => ({ ...p, agenda: items }));
                      setDragIdx(null);
                    }}
                    onDragEnd={() => setDragIdx(null)}
                    className={`flex gap-2 items-start p-3 bg-slate-50 rounded-lg transition-all cursor-grab active:cursor-grabbing ${dragIdx === idx ? 'opacity-50' : ''}`}
                    data-testid={`agenda-item-${idx}`}>
                    <div className="flex-shrink-0 pt-1"><GripVertical className="h-4 w-4 text-slate-400" /></div>
                    <div className="flex-1 grid grid-cols-[100px_1fr] gap-2">
                      <Input placeholder="09:00" value={item.time} onChange={e => updateItem('agenda', idx, 'time', e.target.value)} className="text-sm" data-testid={`agenda-time-${idx}`} />
                      <Input placeholder="Session title" value={item.title} onChange={e => updateItem('agenda', idx, 'title', e.target.value)} className="text-sm" data-testid={`agenda-title-${idx}`} />
                      <div className="col-span-2 space-y-2" data-testid={`agenda-speakers-${idx}`}>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-slate-500 font-semibold uppercase tracking-wide">
                            Speakers {item.speakers?.length ? `(${item.speakers.length}/8)` : ''}
                          </Label>
                          {(item.speakers?.length || 0) < 8 && (
                            <Button type="button" size="sm" variant="outline"
                              onClick={() => addAgendaSpeaker(idx)}
                              data-testid={`agenda-add-speaker-${idx}`}
                              className="h-7 text-xs">
                              <PlusCircle className="h-3 w-3 mr-1" /> Add speaker
                            </Button>
                          )}
                        </div>
                        {(item.speakers || []).length === 0 && (
                          <p className="text-[11px] text-slate-400 italic">No speakers yet. Click "Add speaker" to add up to 8 (e.g. panel members).</p>
                        )}
                        {(item.speakers || []).map((spk, spkIdx) => {
                          const searchKey = `${idx}-${spkIdx}`;
                          const query = agendaSpeakerSearch[searchKey] || '';
                          return (
                            <div key={spkIdx} className="relative p-2 bg-white rounded border border-slate-200" data-testid={`agenda-speaker-${idx}-${spkIdx}`}>
                              <div className="flex gap-2 items-center">
                                <Input
                                  placeholder="Speaker name"
                                  value={spk.name || ''}
                                  onChange={e => updateAgendaSpeaker(idx, spkIdx, 'name', e.target.value)}
                                  onBlur={() => { if ((spk.name || '').trim()) syncSpeakerToDetail(spk); }}
                                  className="text-sm flex-1"
                                  data-testid={`agenda-speaker-name-${idx}-${spkIdx}`}
                                />
                                {spk.user_id && (
                                  <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1 flex-shrink-0">
                                    {spk.headshot_url ? <img src={spk.headshot_url} alt="" className="w-4 h-4 rounded-full object-cover" /> : null}
                                    Tagged
                                    <button type="button" className="ml-1 hover:text-red-500"
                                      onClick={() => clearAgendaSpeakerLink(idx, spkIdx)}
                                      data-testid={`agenda-speaker-clear-${idx}-${spkIdx}`}>
                                      <X className="h-3 w-3" />
                                    </button>
                                  </span>
                                )}
                                <Button size="sm" variant="ghost"
                                  onClick={() => removeAgendaSpeaker(idx, spkIdx)}
                                  className="text-red-400 hover:text-red-600 flex-shrink-0 h-7 w-7 p-0"
                                  data-testid={`agenda-speaker-remove-${idx}-${spkIdx}`}>
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                              {!spk.user_id && (
                                <Input
                                  placeholder="🔗 Tag a member to link their profile…"
                                  value={query}
                                  onChange={e => setAgendaSpeakerSearch(prev => ({ ...prev, [searchKey]: e.target.value }))}
                                  className="text-xs mt-1.5 bg-brand-navy/5"
                                  data-testid={`agenda-speaker-search-${idx}-${spkIdx}`}
                                />
                              )}
                              {query.length >= 2 && (
                                <div className="absolute z-10 top-full left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                                  {(() => {
                                    const matches = allMembers
                                      .filter(m => m.name?.toLowerCase().includes(query.toLowerCase()))
                                      .slice(0, 5);
                                    if (matches.length === 0) {
                                      return <p className="px-3 py-2 text-xs text-slate-400">No members found</p>;
                                    }
                                    return matches.map(m => (
                                      <button key={m.user_id} type="button"
                                        onClick={() => linkMemberToAgendaSpeaker(idx, spkIdx, m)}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                                        data-testid={`agenda-speaker-pick-${idx}-${spkIdx}-${m.user_id}`}>
                                        {m.picture ? (
                                          <img src={m.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                                        ) : (
                                          <div className="w-6 h-6 rounded-full bg-brand-navy/10 flex items-center justify-center text-[10px] font-bold text-brand-navy">{m.name?.charAt(0)}</div>
                                        )}
                                        <span>{m.name}</span>
                                        <span className="text-xs text-slate-400 ml-auto">{m.profile?.role || ''}</span>
                                      </button>
                                    ));
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="col-span-2">
                        <Input placeholder="Room (optional, e.g. Hall A, Breakout 2)" value={item.room || ''} onChange={e => updateItem('agenda', idx, 'room', e.target.value)} className="text-sm" data-testid={`agenda-room-${idx}`} />
                      </div>
                      <div className="col-span-2 flex items-center gap-4 flex-wrap bg-amber-50/60 border border-amber-100 rounded-md px-3 py-2">
                        <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!item.is_britelab}
                            onChange={e => updateItem('agenda', idx, 'is_britelab', e.target.checked)}
                            data-testid={`agenda-britelab-${idx}`}
                            className="w-4 h-4 accent-amber-500" />
                          <span>This is a BriteLab (per-session registration)</span>
                        </label>
                        {item.is_britelab && (
                          <>
                            <div className="flex items-center gap-2">
                              <Label className="text-[11px] text-slate-500 whitespace-nowrap">Max registrants</Label>
                              <Input
                                type="number"
                                min="1"
                                value={item.max_registrants || ''}
                                onChange={e => updateItem('agenda', idx, 'max_registrants', e.target.value)}
                                placeholder="20"
                                className="h-8 w-20 text-xs"
                                data-testid={`agenda-max-${idx}`} />
                              {(() => {
                                const c = editingLabCounts[item.id || stableAgendaItemId(item)];
                                if (!c) return null;
                                const max = parseInt(item.max_registrants, 10) || 0;
                                const left = max ? Math.max(max - c.count, 0) : null;
                                return (
                                  <span data-testid={`agenda-places-left-${idx}`}
                                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${left === 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                    {c.count} registered{left !== null ? ` · ${left === 0 ? 'FULL' : `${left} left`}` : ''}
                                  </span>
                                );
                              })()}
                            </div>
                            {editing?.event_id && (
                              <button
                                type="button"
                                onClick={() => setViewingRegistrants({
                                  event_id: editing.event_id,
                                  item,
                                })}
                                data-testid={`view-registrants-${idx}`}
                                className="ml-auto text-xs font-semibold text-brand-navy hover:underline">
                                View registrants →
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <div className="col-span-2">
                        <RichTextEditor
                          placeholder="Session description — bold key ideas, add bullets, colour highlights…"
                          value={item.description || ''}
                          onChange={v => updateItem('agenda', idx, 'description', v)}
                          testId={`agenda-description-${idx}`}
                          minHeight="5rem"
                        />
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => removeItem('agenda', idx)} data-testid={`remove-agenda-${idx}`}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </section>

            {/* Speakers */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><User className="h-4 w-4" /> Speakers</h3>
                <Button size="sm" variant="outline" onClick={() => addItem('speakers_detail', emptySpeaker)} data-testid="add-speaker-btn">
                  <PlusCircle className="h-3.5 w-3.5 mr-1" /> Add Speaker
                </Button>
              </div>
              {(form.speakers_detail || []).length === 0 && <p className="text-xs text-slate-400 italic">No speakers added yet.</p>}
              <div className="space-y-3">
                {(form.speakers_detail || []).map((spk, idx) => (
                  <div key={idx}
                    draggable
                    onDragStart={() => setSpeakerDragIdx(idx)}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-brand-navy/30'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-brand-navy/30'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('ring-2', 'ring-brand-navy/30');
                      if (speakerDragIdx === null || speakerDragIdx === idx) return;
                      const items = [...(form.speakers_detail || [])];
                      const [moved] = items.splice(speakerDragIdx, 1);
                      items.splice(idx, 0, moved);
                      setForm(p => ({ ...p, speakers_detail: items }));
                      setSpeakerDragIdx(null);
                    }}
                    onDragEnd={() => setSpeakerDragIdx(null)}
                    className={`p-3 bg-slate-50 rounded-lg transition-all cursor-grab active:cursor-grabbing ${speakerDragIdx === idx ? 'opacity-50' : ''}`}
                    data-testid={`speaker-item-${idx}`}>
                    <div className="flex gap-3 items-start">
                      <div className="flex-shrink-0 pt-1" title="Drag to reorder"><GripVertical className="h-4 w-4 text-slate-400" /></div>
                      <div className="w-16 h-16 rounded-full bg-slate-200 flex-shrink-0 overflow-hidden flex items-center justify-center">
                        {spk.headshot_url ? (
                          <img src={spk.headshot_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="h-6 w-6 text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        {/* Member link search */}
                        <div className="relative">
                          <Input
                            placeholder="Search member to link..."
                            value={speakerSearch[idx] || ''}
                            onChange={e => setSpeakerSearch(prev => ({ ...prev, [idx]: e.target.value }))}
                            className="text-sm"
                            data-testid={`speaker-member-search-${idx}`}
                          />
                          {speakerSearch[idx]?.length >= 2 && (
                            <div className="absolute z-10 top-full left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                              {allMembers
                                .filter(m => m.name?.toLowerCase().includes(speakerSearch[idx].toLowerCase()))
                                .slice(0, 5)
                                .map(m => (
                                  <button key={m.user_id} type="button"
                                    onClick={() => linkMemberToSpeaker(idx, m)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                                    data-testid={`speaker-link-${m.user_id}`}>
                                    {m.picture ? (
                                      <img src={m.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                                    ) : (
                                      <div className="w-6 h-6 rounded-full bg-brand-navy/10 flex items-center justify-center text-[10px] font-bold text-brand-navy">{m.name?.charAt(0)}</div>
                                    )}
                                    <span>{m.name}</span>
                                    <span className="text-xs text-slate-400 ml-auto">{m.profile?.role || ''}</span>
                                  </button>
                                ))}
                              {allMembers.filter(m => m.name?.toLowerCase().includes(speakerSearch[idx].toLowerCase())).length === 0 && (
                                <p className="px-3 py-2 text-xs text-slate-400">No members found</p>
                              )}
                            </div>
                          )}
                          {spk.user_id && (
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Linked</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder="Speaker name" value={spk.name} onChange={e => updateItem('speakers_detail', idx, 'name', e.target.value)} className="text-sm" data-testid={`speaker-name-${idx}`} />
                          <Input placeholder="Headshot URL" value={spk.headshot_url} onChange={e => updateItem('speakers_detail', idx, 'headshot_url', e.target.value)} className="text-sm" data-testid={`speaker-headshot-${idx}`} />
                        </div>
                        <RichTextEditor placeholder="Speaker bio — bold their titles, format paragraphs, add links…"
                          value={spk.bio || ''} onChange={v => updateItem('speakers_detail', idx, 'bio', v)}
                          testId={`speaker-bio-${idx}`} minHeight="7rem" />
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => removeItem('speakers_detail', idx)} data-testid={`remove-speaker-${idx}`}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Parking & Additional Info */}
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Car className="h-4 w-4" /> Parking & Additional Information</h3>
              <div className="space-y-3">
                <div><Label>Parking Information</Label><Textarea rows={2} placeholder="Parking details, nearest car parks, public transport options..." value={form.parking_info} onChange={e => setForm({ ...form, parking_info: e.target.value })} data-testid="event-parking-input" /></div>
                <div><Label>Additional Information</Label><Textarea rows={3} placeholder="Dress code, what to bring, dietary requirements, accessibility info..." value={form.additional_info} onChange={e => setForm({ ...form, additional_info: e.target.value })} data-testid="event-additional-input" /></div>
              </div>
            </section>

            {/* Linked Keynotes — attendees of this event auto-gain access to these */}
            <section data-testid="linked-keynotes-section">
              <h3 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2"><Award className="h-4 w-4" /> Linked Keynotes</h3>
              <p className="text-xs text-slate-500 mb-3">Attendees registered for this event will automatically gain access to the keynotes you tick below.</p>
              {allKeynotes.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No keynotes published yet.</p>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto border border-slate-200 rounded p-2">
                  {allKeynotes.map(k => {
                    const checked = (form.linked_keynote_ids || []).includes(k.keynote_id);
                    return (
                      <label key={k.keynote_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                        <input
                          type="checkbox"
                          checked={checked}
                          data-testid={`link-keynote-${k.keynote_id}`}
                          onChange={() => setForm(p => ({
                            ...p,
                            linked_keynote_ids: checked
                              ? p.linked_keynote_ids.filter(id => id !== k.keynote_id)
                              : [...(p.linked_keynote_ids || []), k.keynote_id],
                          }))}
                        />
                        <span className="font-medium">{k.title}</span>
                        {k.speaker_name && <span className="text-xs text-slate-400">· {k.speaker_name}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Event Sponsors */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Award className="h-4 w-4" /> Event Sponsors</h3>
                <Button size="sm" variant="outline" onClick={() => addItem('event_sponsors', emptySponsor)} data-testid="add-event-sponsor-btn">
                  <PlusCircle className="h-3.5 w-3.5 mr-1" /> Add Sponsor
                </Button>
              </div>
              {(form.event_sponsors || []).length === 0 && <p className="text-xs text-slate-400 italic">No sponsors added yet.</p>}
              <div className="space-y-2">
                {(form.event_sponsors || []).map((sp, idx) => (
                  <div key={idx} className="flex gap-2 items-center p-3 bg-slate-50 rounded-lg" data-testid={`event-sponsor-item-${idx}`}>
                    <div className="w-10 h-10 rounded bg-white border border-slate-200 flex-shrink-0 overflow-hidden flex items-center justify-center">
                      {sp.logo_url ? <img src={sp.logo_url} alt="" className="w-full h-full object-contain p-0.5" /> : <Award className="h-4 w-4 text-slate-300" />}
                    </div>
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input placeholder="Sponsor name" value={sp.name} onChange={e => updateItem('event_sponsors', idx, 'name', e.target.value)} className="text-sm" data-testid={`sponsor-name-${idx}`} />
                      <Input placeholder="Logo URL" value={sp.logo_url} onChange={e => updateItem('event_sponsors', idx, 'logo_url', e.target.value)} className="text-sm" />
                      <Input placeholder="Website URL" value={sp.website} onChange={e => updateItem('event_sponsors', idx, 'website', e.target.value)} className="text-sm" />
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => removeItem('event_sponsors', idx)} data-testid={`remove-sponsor-${idx}`}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </section>

            <Button onClick={save} className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="save-event-button">
              {editing ? 'Update Event' : 'Create Event'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <RegistrationsDialog
        open={!!registrationsFor}
        onClose={() => setRegistrationsFor(null)}
        kind="event"
        id={registrationsFor?.id}
        title={registrationsFor?.title}
      />

      <EventAttendanceDialog
        open={!!attendanceFor}
        onClose={() => setAttendanceFor(null)}
        eventId={attendanceFor?.id}
        eventTitle={attendanceFor?.title}
      />

      <BriteLabReportDialog event={britelabReportFor} onClose={() => setBritelabReportFor(null)} />
      <BriteLabRegistrantsDialog
        open={!!viewingRegistrants}
        onClose={() => setViewingRegistrants(null)}
        eventId={viewingRegistrants?.event_id}
        item={viewingRegistrants?.item}
      />

      {/* Historic-attendee bulk import — paste one email per line, server
          resolves to users and adds them to rsvps + attended_events. */}
      <Dialog open={!!historicFor} onOpenChange={(v) => { if (!v) { setHistoricFor(null); setHistoricResult(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="historic-attendees-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Historic Attendees</DialogTitle>
          </DialogHeader>
          {historicFor && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Adding attendees to <span className="font-semibold text-slate-700">{historicFor.title}</span>. Each user will be marked as having attended, will see this event on their profile, and will gain access to any linked keynotes.
              </p>
              <div>
                <Label className="text-xs">Member emails — one per line</Label>
                <Textarea
                  rows={8}
                  value={historicEmails}
                  onChange={e => setHistoricEmails(e.target.value)}
                  placeholder="kelly@example.com&#10;john@example.com&#10;..."
                  data-testid="historic-emails-textarea"
                />
                <p className="text-[10px] text-slate-400 mt-1">Tip: paste a list from a spreadsheet. We'll skip emails that don't match a registered member and report them back.</p>
              </div>
              {historicResult && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 text-xs space-y-1" data-testid="historic-result">
                  <p className="font-semibold text-emerald-700">
                    Added {historicResult.added} of {historicResult.total_resolved} resolved member{historicResult.total_resolved === 1 ? '' : 's'}.
                  </p>
                  {historicResult.not_found_emails?.length > 0 && (
                    <div>
                      <p className="font-medium text-amber-700 mt-1">Not found ({historicResult.not_found_emails.length}):</p>
                      <p className="text-amber-600 break-all">{historicResult.not_found_emails.join(', ')}</p>
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => { setHistoricFor(null); setHistoricResult(null); }}>Close</Button>
                <Button
                  size="sm"
                  onClick={submitHistoricAttendees}
                  disabled={historicBusy}
                  className="bg-gradient-to-r from-brand-navy to-brand-sage text-white"
                  data-testid="submit-historic-attendees"
                >
                  {historicBusy ? 'Adding…' : 'Add Attendees'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
